
import { useState } from "react";
import { toast } from "sonner";
import { gdprService } from "@/services/gdpr/GdprDeletionService";
import type { UserProfile } from "./types";

export function useDeleteUser(refetchUsers: () => void) {
  const [isDeleting, setIsDeleting] = useState(false);
  
  const deleteUser = async (user: UserProfile) => {
    if (!window.confirm(`Are you sure you want to completely delete ${user.name}? This will permanently delete their account and anonymize all their data. This action cannot be undone.`)) return false;
    
    setIsDeleting(true);
    
    try {
      console.log("Attempting complete GDPR deletion for user with ID:", user.id);
      
      // Step 1: Execute Phase 1 (data anonymization)
      const phase1Result = await gdprService.executePhase1(user.id, {
        enableNotifications: false,
        validateCompliance: true,
        autoProcessPhase2: false
      });
      
      if (!phase1Result.success) {
        console.error("Phase 1 (anonymization) failed:", phase1Result.error);
        toast.error("Failed to anonymize user data: " + phase1Result.error);
        return false;
      }
      
      console.log("Phase 1 (anonymization) completed successfully");
      
      // Step 2: Execute Phase 2 (auth deletion)
      const phase2Result = await gdprService.executePhase2(user.id, {
        enableNotifications: false,
        validateCompliance: true
      });
      
      if (!phase2Result.success) {
        console.error("Phase 2 (auth deletion) failed:", phase2Result.error);
        toast.error("Failed to delete user account: " + phase2Result.error);
        return false;
      }
      
      console.log("Phase 2 (auth deletion) completed successfully");
      
      // Refresh the users list
      refetchUsers();
      
      console.log("User deleted successfully");
      toast.success("User account deleted completely and GDPR-compliant");
      return true;
      
    } catch (error: any) {
      console.error("Error deleting user:", error.message);
      toast.error("Unexpected error during user deletion");
      return false;
    } finally {
      setIsDeleting(false);
    }
  };
  
  return { deleteUser, isDeleting };
}
